import { Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { AUTOMATION_CREATION_STEPS, MAX_FEATURE_NAME_LENGTH } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { paths } from '@hero/routes/paths';
import { useUpdateAutomation } from '@hero/services';
import { useNewAutomationStore } from '@hero/store/hooks';

const InputLabel = styled.label`
  background-color: var(--color-accent-secondary-weakest);
  color: var(--color-accent-secondary-default);
  font-size: 1rem;
  line-height: 100%;
  padding: 0 8px;
  position: absolute;
  top: -5px;
  transform: translateX(-9px);
`;

const EditableArea = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 100%;
  padding: 8px;
  padding-left: 16px;
  position: absolute;
  width: 305px;

  &:hover {
    border: 1px solid var(--color-accent-secondary-weaker);
    border-radius: var(--size-border-radius-md);
  }
`;

const ActionButton = styled.button`
  align-items: center;
  background: var(--color-bg-body);
  border: 1px solid var(--color-accent-secondary-weaker);
  border-radius: var(--size-border-radius-md);
  box-shadow: var(--shadow-s);
  cursor: ${({ inactive }) => (inactive ? `not-allowed` : `pointer`)};
  display: flex;
  height: 32px;
  justify-content: center;
  margin-left: 3px;
  width: 32px;

  svg path {
    fill: ${({ inactive }) => (inactive ? `var(--color-accent-secondary-default)` : '')};
  }
`;

const ActionButtons = styled.div`
  bottom: -15px;
  display: flex;
  position: absolute;
  right: -8px;
`;

const DetailItem = styled.div`
  position: absolute;
  width: 305px;
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 40px;
  position: relative;
  width: 250px;

  ${Input.S.Base} {
    width: 100%;
  }
`;

const getAutomationSteps = () => [
  {
    title: 'gifts',
    icon: <Icon name="gift-box" />,
    route: paths.NEW_AUTOMATION_GIFTS,
    routeName: AUTOMATION_CREATION_STEPS.newAutomationGifts
  },
  {
    title: 'rules',
    icon: <Icon name="rule" />,
    route: paths.NEW_AUTOMATION_RULES,
    routeName: AUTOMATION_CREATION_STEPS.newAutomationRules
  },
  {
    title: 'recipients',
    icon: <Icon name="contacts" />,
    route: paths.NEW_AUTOMATION_RECIPIENTS,
    routeName: AUTOMATION_CREATION_STEPS.newAutomationRecipients
  },
  {
    title: 'experience',
    icon: <Icon name="smartphone" />,
    route: paths.NEW_AUTOMATION_EXPERIENCE,
    routeName: AUTOMATION_CREATION_STEPS.newAutomationExperience
  }
];

function useAutomationCreationHeader({ step, setIsPageLoading }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    addons,
    code,
    contacts,
    experienceSettings: { messageParams, branding, occasion } = {},
    settings,
    products,
    name,
    type,
    updateName,
    reset
  } = useNewAutomationStore();
  const automationSteps = getAutomationSteps();
  const { mutateAsync: updateAutomation } = useUpdateAutomation();

  const [edit, setEdit] = useState(false);
  const [automationName, setAutomationName] = useState(name);
  const [inputRef, setInputRef] = useState(null);

  async function updateAutomationWithLoading() {
    setIsPageLoading(true);

    await updateAutomation({
      params: {
        code
      },
      body: {
        addons,
        name: automationName,
        type,
        products,
        contacts,
        experienceSettings: { messageParams, branding, occasion },
        settings: {
          recipientVerification: settings.recipientVerification
        },
        meta: {
          creationStep: automationSteps[step - 1].routeName
        }
      }
    });
    setIsPageLoading(false);
  }

  useEffect(() => {
    inputRef?.focus();
  }, [inputRef]);

  return {
    title: (
      <TitleWrapper>
        {!edit ? (
          <EditableArea>
            <InputLabel>{t('automationName')}</InputLabel>
            <Typography
              variant="body3"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {name}
            </Typography>
            <Icon name="edit" style={{ cursor: 'pointer' }} onClick={() => setEdit(true)} />
          </EditableArea>
        ) : (
          <DetailItem>
            <Input
              ref={setInputRef}
              label={t('automationName')}
              value={automationName}
              variant="outlined"
              onChange={e => setAutomationName(e.target.value)}
              error={!automationName}
              inputProps={{ maxLength: MAX_FEATURE_NAME_LENGTH }}
            />
            <ActionButtons>
              <ActionButton
                onClick={() => {
                  setEdit(false);
                  setAutomationName(name);
                }}
              >
                <Icon name="close" />
              </ActionButton>
              <ActionButton
                disabled={!automationName}
                onClick={async () => {
                  setEdit(false);
                  if (name !== automationName) {
                    updateName(automationName);

                    await updateAutomation({
                      params: {
                        code
                      },
                      body: {
                        name: automationName
                      }
                    });
                  }
                }}
              >
                <Icon name="success" />
              </ActionButton>
            </ActionButtons>
          </DetailItem>
        )}
      </TitleWrapper>
    ),
    step,
    showBorder: true,
    steps: automationSteps,
    endButton: {
      children: t('saveAndClose'),
      onClick: async () => {
        await updateAutomationWithLoading();
        navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
        reset();
      }
    },
    style: {
      backgroundColor: 'var(--color-bg-weakest2)'
    },
    showSalesNumber: true
  };
}

export { useAutomationCreationHeader };
